// SyncContext.tsx
import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { sync } from "../libs/syncClient";
import { useAuth } from './AuthContext';
import { useOffline } from './OfflineContext';
import { syncService } from '../services/sync';  // Shared sync service
import { authService } from '../services/auth';
import { localRepositoryEvents } from '../services/storage/RepositoryChangeEventEmitter';
import type { TableRealtimeSync } from '@shared/libs/TableRealtimeSync';

// 1. (Opcional) Definir el tipo para el valor del contexto
interface SyncContextType {
  sync: any; // Reemplaza 'any' con el tipo correcto de tu objeto 'sync'
  ready: boolean;
  isOnline: boolean;
  retryWithBackoff: (fn: () => Promise<void>) => Promise<void>;
  getRealtime?: () => TableRealtimeSync | null; // Instancia de TableRealtimeSync (multi-tabla)
}

// Usamos 'undefined' como valor inicial para un chequeo más seguro en el hook
export const SyncContext = createContext<SyncContextType | undefined>(undefined);

interface SyncProviderProps {
  children: ReactNode;
}

export const SyncProvider = ({ children }: SyncProviderProps) => {
  const [ready, setReady] = useState<boolean>(false);
  const { user, activeEmpresaId } = useAuth();
  const { isOnline, retryWithBackoff } = useOffline();

  // Usar useRef para evitar múltiples inicializaciones durante los cambios de usuario
  const initializingRef = useRef<boolean>(false);
  const lastUserRef = useRef<string | null>(null);
  const lastEmpresaRef = useRef<string | null>(null); // Empresa a la que realtime está conectado
  const userIdRef = useRef<string | null>(null); // Usuario actual (para el listener de profiles)
  const realtimeInitInFlightRef = useRef<boolean>(false); // Evita re-init concurrente para la misma empresa

  /**
   * Resuelve la empresa activa del usuario y (re)conecta realtime si corresponde.
   *
   * Orden de prioridad para resolver `empresaId`:
   *   1. `empresaIdHint` pasado por el llamador (estado `activeEmpresaId` de
   *      AuthContext). Es la fuente más fresca y evita la carrera de leer el
   *      ProfileRepository local cuando AuthContext acaba de cambiar la empresa
   *      (auto-switch por suspensión) pero el caché local aún no se actualizó.
   *   2. Profile local / fetch en reintentos (fallback para el arranque inicial
   *      y para el flujo de refresh-token donde el contexto puede no tener
   *      `activeEmpresaId` todavía).
   */
  const ensureRealtimeForUser = async (
    userId: string,
    empresaIdHint?: string | null,
  ): Promise<void> => {
    let empresaId: string | null = empresaIdHint ?? null;

    // Reintentos para cubrir el caso en que el profile aún no esté disponible
    for (let attempt = 0; attempt < 3 && !empresaId; attempt++) {
      try {
        const profile = await authService.loadUserProfile(userId);
        empresaId = profile?.empresa_activa_id || null;
      } catch (error) {
        console.error('[SyncContext] Error cargando profile para realtime:', error);
      }

      if (!empresaId && attempt < 2) {
        console.warn(`[SyncContext] empresa_activa_id no disponible aún, reintentando (${attempt + 1}/3)...`);
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
    }

    if (!empresaId) {
      console.error('[SyncContext] No se pudo obtener empresa_activa_id; realtime NO se inicializará.');
      return;
    }

    // Ya inicializamos (o estamos inicializando) realtime para esta empresa.
    // No volver a tocarlo: el canal realtime es async (pasa por INITIAL_SYNC
    // antes de SUBSCRIBED), así que isRealtimeActive() es false durante ese
    // lapso. Re-llamar acá mataba el WebSocket en pleno handshake y provocaba
    // el churn de reconexiones. TableRealtimeSync reintenta por sí solo ante
    // errores transitorios (TIMED_OUT/CHANNEL_ERROR).
    if (empresaId === lastEmpresaRef.current) {
      if (realtimeInitInFlightRef.current) {
        console.log('[SyncContext] Init de realtime ya en curso para empresa', empresaId, '- omitiendo');
      }
      return;
    }

    // Si hay un init en curso para OTRA empresa, dejamos que termine: el
    // siguiente evento (o el retry interno) resolverá la empresa nueva.
    if (realtimeInitInFlightRef.current) {
      console.log('[SyncContext] Init de realtime en curso para otra empresa - omitiendo por ahora');
      return;
    }

    realtimeInitInFlightRef.current = true;
    try {
      console.log('[SyncContext] Inicializando realtime (multi-tabla) para empresa:', empresaId);
      await sync.initRealtime(empresaId);
      lastEmpresaRef.current = empresaId;
    } finally {
      realtimeInitInFlightRef.current = false;
    }
  };

  useEffect(() => {
    const initSync = async () => {
      const currentUserId = user?.id ?? null;

      // Si no hay usuario, limpiar estado
      if (!user) {
        console.log("No hay usuario, estado de sincronización reiniciado.");
        setReady(false);
        initializingRef.current = false;
        lastUserRef.current = null;
        lastEmpresaRef.current = null;
        userIdRef.current = null;
        realtimeInitInFlightRef.current = false;
        // Desconectar realtime (esperar para no dejar canal colgado)
        await sync.disconnectRealtime?.();
        return;
      }

      // Si es el mismo usuario, no re-inicializar el sync base
      if (currentUserId === lastUserRef.current) {
        console.log("[SyncContext] Mismo usuario detectado, omitiendo re-inicialización.");
        return;
      }

      // Si ya estamos inicializando, evitar múltiples inicializaciones simultáneas
      if (initializingRef.current) {
        console.log("[SyncContext] Ya hay una inicialización en progreso, omitiendo.");
        return;
      }

      console.log("Usuario detectado:", currentUserId);
      userIdRef.current = currentUserId;
      initializingRef.current = true;
      lastUserRef.current = currentUserId;

      try {
        console.log("iniciando sincronización...");
        await sync.init();
        setReady(true);

        // Inicializar sincronización en tiempo real (multi-tabla).
        // No bloquea la inicialización general si falla: se intenta de forma independiente.
        ensureRealtimeForUser(user.id).catch((error) => {
          console.error('[SyncContext] Error inicializando realtime:', error);
        });
      } catch (error) {
        console.error("[SyncContext] Error durante inicialización:", error);
      } finally {
        initializingRef.current = false;
      }
    };

    initSync();
  // Solo depender de 'user', no de 'ready'
  }, [user]);

  // Reaccionar al cambio de empresa activa: garantizar que el realtime siga a
  // la empresa activa. Es el único dueño del ciclo de vida del realtime
  // (reloadAfterCompanySwitch sólo recarga datos).
  //
  // Pasamos `activeEmpresaId` directamente al helper para que use el estado de
  // React (fuente más fresca) en vez de releer el ProfileRepository local,
  // que puede estar stale justo después de un auto-switch por suspensión.
  //
  // Idempotente: ensureRealtimeForUser → initRealtime corta si ya está
  // conectado a la misma empresa, así que este effect puede dispararse sin
  // riesgo de reconexiones duplicadas (también en el mount inicial).
  useEffect(() => {
    if (!user) return;
    ensureRealtimeForUser(user.id, activeEmpresaId).catch((error) => {
      console.error('[SyncContext] Error inicializando realtime tras cambio de empresa:', error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeEmpresaId]);

  // Safety net: cuando AuthContext finalmente escribe el profile local (async,
  // no esperado), este evento se dispara y garantiza que realtime se inicialice
  // para la empresa correcta, cubriendo la carrera del refresh-token.
  useEffect(() => {
    const unsubscribe = localRepositoryEvents.subscribe((event) => {
      if (event.table !== 'profiles') return;

      const userId = userIdRef.current;
      if (!userId) return;

      // Si realtime ya está activo para una empresa, no hacer nada.
      if (sync.isRealtimeActive?.()) return;

      console.log('[SyncContext] Profile local actualizado, inicializando realtime...');
      ensureRealtimeForUser(userId).catch((error) => {
        console.error('[SyncContext] Error inicializando realtime tras evento de profile:', error);
      });
    });

    return () => unsubscribe();
  }, []);

  // Método para obtener la instancia de TableRealtimeSync (multi-tabla)
  const getRealtime = () => {
    return sync.getRealtime?.() || null;
  };

  const value = { sync, ready, isOnline, retryWithBackoff, getRealtime };

  return (
    <SyncContext.Provider value={value}>
      {children}
    </SyncContext.Provider>
  );
};

// 2. (Recomendado) Exportar un hook personalizado para consumir el contexto
export const useSync = (): SyncContextType => {
  const context = useContext(SyncContext);
  if (context === undefined) {
    throw new Error('useSync debe ser usado dentro de un SyncProvider');
  }
  return context;
}